import React, { FunctionComponent, useCallback, useState } from 'react';
import {
    Constrain,
    HeadingLevel1,
    pullbackMarginsWide,
    pullbackMarginToNormalConstrainFromNarrow,
    Swell
} from '../ui';
import styled from '../../styled-components';
import { media } from '../../utilities';
import { navigate } from 'gatsby-link';
import { InlineWidget } from 'react-calendly';

function encode(data: { [key: string]: string }): string {
    return Object.keys(data)
        .map(
            key => encodeURIComponent(key) + '=' + encodeURIComponent(data[key])
        )
        .join('&');
}

const Heading = styled(HeadingLevel1)`
    margin-bottom: 6rem;

    ${media.m`
        margin-bottom: 7rem;
    `}
`;

const BookASessionContainer = styled.div`
    .calendly-inline-widget {
        height: 560px;

        ${media.m`
            height: 810px;
        `}

        ${media.xl`
            height: 730px;
        `}
    }

    form p {
        margin-bottom: 2rem;
        &:last-child {
            margin-bottom: 0;
        }
    }

    label {
        font-weight: 500;
        display: block;
        margin-bottom: 1rem;
    }

    input {
        display: block;
        width: 100%;
        font-size: 1.6rem;
        padding: 1rem;
        color: ${({ theme }) => theme.colors.deepBlue.color};
        border: none;
        min-height: 4.3rem;

        ${media.m`
            width: 50%;
        `}
        ${media.l`
            width: 100%;
        `}

        &:focus {
            outline: 2px solid ${({ theme }) => theme.colors.electricBlue.color};
        }
    }

    button {
        text-align: center;
        display: block;
        width: 100%;
        color: ${({ theme }) => theme.colors.deepBlue.color};
        background-color: ${({ theme }) => theme.colors.electricBlue.color};
        border: 0;
        margin-top: 6rem;
        font-weight: 500;
        padding: 1rem;
        min-height: 4.3rem;

        &:hover {
            background-color: #80f4f4;
        }

        &:active {
            background-color: #04bfbe;
        }

        ${media.m`
            padding: 1rem 3rem;
            width: auto;
        `}
    }

    .hidden {
        display: none;
    }

    .inner {
        ${media.l`
            ${pullbackMarginsWide}
        `}

        ${media.xl`
            ${pullbackMarginToNormalConstrainFromNarrow}
        `}
    }

    .booking-options {
        & > div:first-child {
            border-bottom: 1px solid rgba(255, 255, 255, 0.5);
            margin-bottom: 3rem;
        }

        ${media.l`
            display: flex;
            margin: 0 -2rem;
            position: relative;

            & > div {
                padding: 0 2rem;
                ${media.xl`
                    padding: 0 4rem;
                `}
            }

            & > div:first-child {
                flex: 60% 0 0;
                border-bottom: none;
                margin-bottom: 0;
            }

            & > div:last-child {
                flex: 40% 0 0;
            }


            &:after {
                content: '';
                position: absolute;
                top: 0;
                bottom: 0;
                width: 1px;
                background: #fff;
                opacity: 0.3;
                left: calc(60% - 1px);
            }
        `}

        ${media.xl`
            margin: 0 -4rem;
        `}
    }
`;

type BookACallProps = {
    calendlyUrl: string;
    pageUrl:
        | 'the-wave-machine-digital-product-clinic'
        | 'helping-organisations-improve-an-existing-process'
        | 'helping-organisations-fill-a-gap-in-a-team'
        | 'entrepreneur-with-an-idea-for-innovative-web-or-mobile-app';
};

export const BookACall: FunctionComponent<BookACallProps> = ({
    calendlyUrl,
    pageUrl
}) => {
    const [bofField, setBotField] = useState<string>('');
    const [nameField, setNameField] = useState<string>('');
    const [emailField, setEmailField] = useState<string>('');
    const [phoneField, setPhoneField] = useState<string>('');

    const handleSubmit = useCallback(
        e => {
            e.preventDefault();
            const form = e.target;
            fetch('/', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded'
                },
                body: encode({
                    'form-name': form.getAttribute('name'),
                    'name': nameField,
                    'email': emailField,
                    'phone': phoneField,
                    'bot-field': bofField
                })
            })
                .then(() => navigate(form.getAttribute('action')))
                .catch(error => alert(error));
        },
        [bofField, nameField, emailField, phoneField]
    );

    return (
        <BookASessionContainer>
            <Constrain type="narrow">
                <div className="inner">
                    <div className="booking-options">
                        <div>
                            <h4>Book a time directly from our calendar.</h4>

                            <InlineWidget
                                url={calendlyUrl}
                                styles={
                                    {
                                        /* reset default*/
                                    }
                                }
                            />
                        </div>
                        <div>
                            <h4>Can’t find a time that suits?</h4>
                            <p>
                                Drop us a line below and we’ll contact you to
                                organise a time that works.
                            </p>

                            <form
                                action={`/${pageUrl}/thanks/`}
                                name={pageUrl}
                                netlify-honeypot="bot-field"
                                data-netlify
                                method="POST"
                                onSubmit={handleSubmit}
                            >
                                <p>
                                    <label htmlFor="name">Name</label>
                                    <input
                                        type="text"
                                        name="name"
                                        onChange={e =>
                                            setNameField(e.target.value)
                                        }
                                        required
                                    />
                                </p>
                                <p>
                                    <label htmlFor="email">Email address</label>
                                    <input
                                        type="email"
                                        name="email"
                                        onChange={e =>
                                            setEmailField(e.target.value)
                                        }
                                        required
                                    />
                                </p>
                                <p>
                                    <label htmlFor="phone">
                                        Phone number (optional)
                                    </label>
                                    <input
                                        type="tel"
                                        name="phone"
                                        onChange={e =>
                                            setPhoneField(e.target.value)
                                        }
                                    />
                                </p>
                                <p className="hidden">
                                    <label>
                                        Don’t fill this out if you're human:{' '}
                                        <input
                                            name="bot-field"
                                            type="text"
                                            onChange={e =>
                                                setBotField(e.target.value)
                                            }
                                        />
                                    </label>
                                </p>
                                <p>
                                    <button type="submit">Submit</button>
                                </p>
                            </form>
                        </div>
                    </div>
                </div>
            </Constrain>
        </BookASessionContainer>
    );
};
